import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PageMeta from '../../../components/base/PageMeta';
import JsonLd from '../../../components/base/JsonLd';
import BrandLogo from '../../../components/base/BrandLogo';
import FloatingCtaBar from '../../../components/feature/FloatingCtaBar';

const tools = [
  {
    name: 'Gopuff',
    slug: 'gopuff',
    logoUrl: 'https://icon.horse/icon/gopuff.com',
    tagline: 'Instant Delivery for Groceries, Food & Essentials',
    desc: 'Gopuff is a pioneering instant delivery platform that operates its own micro-fulfillment centers to deliver thousands of grocery, snack, and household items to your door in under 30 minutes — 24/7 across hundreds of US cities.',
    tags: ['Instant Delivery', 'Grocery', 'Snacks', 'Beverages'],
    rating: 4.6,
    reviews: 12400,
    price: 'Free · $2.95 delivery fee',
    priceType: 'delivery',
    link: 'https://gopuff.pxf.io/RGbXg9',
    logoIcon: 'ri-motorbike-line',
    logoGrad: 'from-emerald-500 to-teal-600',
    badge: 'Top Pick',
    badgeColor: 'bg-emerald-100 text-emerald-700',
  },
  {
    name: "Nature's Lab",
    slug: 'natures-lab',
    logoUrl: 'https://icon.horse/icon/natureslab.com',
    tagline: 'Premium Quality Vitamins & Nutritional Supplements',
    desc: "Nature's Lab delivers science-backed nutritional supplements manufactured in a cGMP-certified USA facility with third-party testing for purity and potency. Comprehensive range from vitamins and minerals to specialty formulations.",
    tags: ['Vitamins', 'Supplements', 'Wellness', 'Nutrition'],
    rating: 4.7,
    reviews: 3820,
    price: 'From $14.99',
    priceType: 'nutrition',
    link: 'https://www.natureslab.com/NHAN21364',
    logoIcon: 'ri-leaf-line',
    logoGrad: 'from-green-500 to-emerald-600',
    badge: "Editor's Choice",
    badgeColor: 'bg-green-100 text-green-700',
  },
  {
    name: 'Paleo On The Go',
    slug: 'paleo-on-the-go',
    logoUrl: 'https://icon.horse/icon/paleonthego.com',
    tagline: 'Chef-Prepared Paleo & AIP Frozen Meals Delivered',
    desc: 'Paleo On The Go offers chef-crafted frozen meals strictly adhering to Paleo and Autoimmune Protocol (AIP) dietary frameworks. Made from 100% real food ingredients — grass-fed meats, wild-caught seafood, and organic vegetables.',
    tags: ['Paleo', 'AIP', 'Meal Delivery', 'Autoimmune'],
    rating: 4.5,
    reviews: 2190,
    price: 'From $9.99/meal',
    priceType: 'delivery',
    link: 'https://paleonthego.com/?rfsn=8625899.51166cf&utm_source=refersion&utm_medium=affiliate&utm_campaign=8625899.51166cf',
    logoIcon: 'ri-restaurant-line',
    logoGrad: 'from-amber-500 to-orange-500',
    badge: '',
    badgeColor: '',
  },
  {
    name: "Pete's Paleo",
    slug: 'petes-paleo',
    logoUrl: 'https://icon.horse/icon/petesrealfood.com',
    tagline: 'Farm-Fresh Paleo Meal Kits & Ready-Made Meals',
    desc: "Pete's Paleo sources ingredients exclusively from local sustainable farms, delivering freshly prepared Paleo meals featuring pasture-raised proteins and seasonal organic vegetables. Supports Whole30, SCD, and GAPS diet protocols.",
    tags: ['Farm-Fresh', 'Paleo', 'Whole30', 'Organic'],
    rating: 4.6,
    reviews: 1870,
    price: 'From $11.99/meal',
    priceType: 'delivery',
    link: 'https://www.petesrealfood.com/?rfsn=8625890.8fd34a6&utm_source=refersion&utm_medium=affiliate&utm_campaign=8625890.8fd34a6',
    logoIcon: 'ri-seedling-line',
    logoGrad: 'from-lime-500 to-green-600',
    badge: 'Popular',
    badgeColor: 'bg-lime-100 text-lime-700',
  },
  {
    name: 'Nutri-Genetix',
    slug: 'nutri-genetix',
    logoUrl: 'https://icon.horse/icon/nutri-genetix.com',
    tagline: 'Personalized Nutrition Science Powered by Your DNA',
    desc: "Nutri-Genetix uses cutting-edge nutrigenomics to create customized protein and supplement formulas based on individual DNA profiles. A simple DNA test kit unlocks your personal genetic blueprint for optimal nutrition.",
    tags: ['DNA Nutrition', 'Personalized', 'Protein', 'Science'],
    rating: 4.5,
    reviews: 1540,
    price: 'From £39.99',
    priceType: 'nutrition',
    link: 'https://www.nutri-genetix.com/?ref=upnhqvhm',
    logoIcon: 'ri-dna-line',
    logoGrad: 'from-teal-500 to-cyan-600',
    badge: 'New',
    badgeColor: 'bg-teal-100 text-teal-700',
  },
  {
    name: 'Simply Straws',
    slug: 'simply-straws',
    logoUrl: 'https://icon.horse/icon/simplystraws.com',
    tagline: 'Eco-Friendly Reusable Glass & Steel Straws',
    desc: "Simply Straws crafts premium reusable glass, stainless steel, and bamboo straws as eco-conscious alternatives to single-use plastic. Made from durable borosilicate glass with beautiful designs and a cleaning brush included in every set.",
    tags: ['Eco-Friendly', 'Zero Waste', 'Kitchen', 'Sustainable'],
    rating: 4.4,
    reviews: 2860,
    price: 'From $4.99',
    priceType: 'kitchen',
    link: 'https://simplystraws.com/?ref=HAONGUYENTHITU',
    logoIcon: 'ri-leaf-line',
    logoGrad: 'from-emerald-400 to-green-500',
    badge: '',
    badgeColor: '',
  },
  {
    name: 'PARIS RHÔNE',
    slug: 'parisrhone',
    logoUrl: 'https://icon.horse/icon/parisrhone.com',
    tagline: 'Smart Kitchen Appliances for Modern Home Cooks',
    desc: 'PARIS RHÔNE combines sophisticated French design with smart technology across a full range of kitchen appliances including air fryers, toaster ovens, blenders, and electric kettles. App-connected precision cooking at accessible pricing.',
    tags: ['Kitchen Appliances', 'Smart Home', 'Air Fryer', 'Design'],
    rating: 4.5,
    reviews: 4120,
    price: 'From $29.99',
    priceType: 'kitchen',
    link: 'https://parisrhonecom.sjv.io/OeGkbZ',
    logoIcon: 'ri-fridge-line',
    logoGrad: 'from-slate-600 to-gray-700',
    badge: 'Trending',
    badgeColor: 'bg-gray-100 text-gray-700',
  },
  {
    name: 'Restaurant.com',
    slug: 'restaurant-com',
    logoUrl: 'https://icon.horse/icon/restaurant.com',
    tagline: "America's Largest Dining Deals & Restaurant Voucher Marketplace",
    desc: "Restaurant.com is the US's largest dining deals marketplace offering discounted vouchers redeemable at 30,000+ restaurants nationwide. Typical savings of 60–80% off dining costs, plus restaurant discovery, reviews, and gift card options.",
    tags: ['Dining Deals', 'Vouchers', 'Restaurants', 'Savings'],
    rating: 4.3,
    reviews: 8750,
    price: 'Vouchers from $10',
    priceType: 'dining',
    link: 'https://restaurantcom.sjv.io/55adYL',
    logoIcon: 'ri-store-2-line',
    logoGrad: 'from-red-500 to-rose-600',
    badge: 'Hot',
    badgeColor: 'bg-red-100 text-red-700',
  },
  {
    name: 'LVLY SEA',
    slug: 'lvly-sea',
    logoUrl: 'https://icon.horse/icon/lvly.com.au',
    tagline: 'Fresh Flower & Gourmet Gift Delivery Across Southeast Asia',
    desc: "LVLY SEA delivers premium fresh flower arrangements and gourmet gift hampers across Southeast Asia with same-day delivery available. Bespoke gifting options including personalized messages, premium chocolates, wine, and artisanal products.",
    tags: ['Flowers', 'Gift Delivery', 'Hampers', 'Southeast Asia'],
    rating: 4.7,
    reviews: 3290,
    price: 'From $35',
    priceType: 'dining',
    link: 'https://lvly.pxf.io/2anod0',
    logoIcon: 'ri-plant-line',
    logoGrad: 'from-pink-500 to-rose-500',
    badge: '',
    badgeColor: '',
  },
  {
    slug: '120life',
    logoUrl: 'https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/ac8ef966-440d-4f60-9da3-368de3c8958b_120Life.png?v=2f9b5038c808055eb6680888d4069399',
    tagline: 'Natural Blood Pressure Support Drink — Lower Your Numbers in 2 Weeks',
    desc: '120Life is a doctor-recommended natural blood pressure support drink with 70,000+ happy customers and 1,400+ physician endorsements. Featured on WSJ, Fox News, ESPN, HGTV, and CNBC. Powered by pomegranate, hibiscus, and grape seed extract — backed by a money-back guarantee.',
    tags: ['Blood Pressure', 'Heart Health', 'Natural Drink', 'Supplements', 'Wellness'],
    rating: 4.8,
    reviews: 4200,
    price: 'From $49.95',
    priceType: 'nutrition',
    link: 'https://www.120life.com/?ref=HAONGUYEN',
    logoIcon: 'ri-heart-pulse-line',
    logoGrad: 'from-red-500 to-rose-600',
    badge: '120Life',
    badgeColor: 'bg-red-100 text-red-700',
  },
];

const relatedCategories = [
  {
    icon: 'ri-heart-pulse-line',
    grad: 'from-pink-500 to-rose-600',
    name: 'Health – Fitness – Wellness',
    desc: 'Fitness tracking, health apps, and wellness management platforms',
    count: 62,
    href: '/categories/health-fitness-wellness',
  },
  {
    icon: 'ri-group-line',
    grad: 'from-yellow-400 to-orange-400',
    name: 'Family – Pet – Kids',
    desc: 'Family apps, pet care platforms, and kids educational tools',
    count: 58,
    href: '/categories/family-pet-kids',
  },
  {
    icon: 'ri-shopping-cart-2-line',
    grad: 'from-orange-400 to-amber-500',
    name: 'Ecommerce & Shopping',
    desc: 'Online stores, marketplaces, and shopping platforms',
    count: 126,
    href: '/categories/ecommerce-shopping',
  },
];

type FilterTab = 'all' | 'delivery' | 'nutrition' | 'kitchen' | 'dining';
type SortOption = 'highest_rating' | 'most_reviews' | 'default';

const filterLabels: Record<FilterTab, string> = {
  all: 'All',
  delivery: 'Food Delivery',
  nutrition: 'Nutrition',
  kitchen: 'Kitchen',
  dining: 'Dining & Gifts',
};

export default function FoodBeveragePage() {
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDone, setSignUpDone] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [sortBy, setSortBy] = useState<SortOption>('highest_rating');
  const [sortOpen, setSortOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';

  const sortLabels: Record<SortOption, string> = {
    highest_rating: 'Highest rating',
    most_reviews: 'Most reviews',
    default: 'Default',
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = tools
    .filter((t) => filterTab === 'all' || t.priceType === filterTab)
    .sort((a, b) => {
      if (sortBy === 'highest_rating') return b.rating - a.rating;
      if (sortBy === 'most_reviews') return b.reviews - a.reviews;
      return 0;
    });

  const renderStars = (rating: number) => {
    const full = Math.floor(rating);
    const half = rating - full >= 0.5;
    return Array.from({ length: 5 }).map((_, i) => {
      if (i < full) return <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>;
      if (i === full && half) return <i key={i} className="ri-star-half-line text-amber-400 text-xs"></i>;
      return <i key={i} className="ri-star-line text-gray-300 text-xs"></i>;
    });
  };

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Food & Beverage Tools | 247digitrend',
    url: `${siteUrl}/categories/food-beverage`,
    description: 'Discover and compare the best food delivery platforms, nutritional supplements, kitchen appliances, and dining deals. 9 curated services with expert reviews.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Categories', item: `${siteUrl}/categories` },
        { '@type': 'ListItem', position: 3, name: 'Food & Beverage', item: `${siteUrl}/categories/food-beverage` },
      ],
    },
  };

  return (
    <div className="min-h-screen" style={{ background: '#f0fdf4', fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title="Best Food & Beverage Services (2026) – Compare & Reviews | 247digitrend"
        description="Compare the best food delivery, nutrition, kitchen appliance, and dining platforms in 2026. Expert reviews of Gopuff, Nature's Lab, Paleo On The Go, PARIS RHÔNE, Restaurant.com and more."
        canonical={`${siteUrl}/categories/food-beverage`}
        keywords="best food delivery 2026, nutrition supplements review, kitchen appliances comparison, dining deals, Gopuff review, Paleo meal delivery"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* SIGN UP MODAL */}
      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowSignUp(false); setSignUpDone(false); }}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-600 text-base"></i>
            </button>
            {signUpDone ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-emerald-100">
                  <i className="ri-check-line text-emerald-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">Registration Successful!</h3>
                <p className="text-sm text-gray-500">Welcome to 247digitrend.</p>
                <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="mt-6 w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">Close</button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500">
                    <i className="ri-restaurant-2-line text-white text-base"></i>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
                  <p className="text-xs text-gray-500">Join 50K+ users of 247digitrend</p>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setSignUpDone(true); }} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                    <input type="email" required value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-emerald-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <input type="password" required value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-emerald-400 transition-colors" />
                  </div>
                  <button type="submit" className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap mt-2">Sign Up Now</button>
                </form>
                <p className="text-xs text-center text-gray-400 mt-4">By signing up, you agree to our <a href="#" className="text-emerald-600 underline">Terms</a>.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100' : 'bg-white border-b border-gray-100'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[15px] font-medium text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[15px] font-semibold text-emerald-600 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[15px] font-medium text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[15px] font-medium text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[15px] font-medium text-gray-700 hover:text-emerald-600 transition-colors cursor-pointer">Blog</a>
            <button className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-search-line text-gray-500 text-sm"></i>
            </button>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-emerald-50 text-emerald-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* PAGE CONTENT */}
      <main className="pt-28 pb-16">
        <div className="max-w-5xl mx-auto px-6">

          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 mb-6">
            <a href="/" className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:border-emerald-300 transition-colors cursor-pointer">
              <i className="ri-home-4-line text-gray-500 text-xs"></i>
            </a>
            <i className="ri-arrow-right-s-line text-gray-400 text-sm"></i>
            <a href="/categories" className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 hover:border-emerald-300 transition-colors cursor-pointer">
              <i className="ri-apps-2-line text-gray-500 text-xs"></i>
            </a>
          </nav>

          {/* Category Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Food &amp; Beverage</h1>
            <p className="text-sm text-gray-500 mb-4">Food delivery platforms, nutritional supplements, kitchen appliances, dining deals, and culinary tools</p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1.5">
                <i className="ri-tools-line text-gray-500 text-xs"></i>
                <span className="text-xs font-semibold text-gray-700">{tools.length} services</span>
              </div>
              <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1.5">
                <i className="ri-star-fill text-amber-500 text-xs"></i>
                <span className="text-xs font-semibold text-emerald-700">Highly rated</span>
              </div>
            </div>
          </div>

          {/* Filter + Sort Bar */}
          <div className="flex items-center justify-between mb-6">
            {/* Filter tabs */}
            <div className="flex items-center bg-white border border-gray-200 rounded-full px-1 py-1 gap-0.5">
              {(Object.keys(filterLabels) as FilterTab[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setFilterTab(tab)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    filterTab === tab
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {filterLabels[tab]}
                </button>
              ))}
            </div>

            {/* Sort dropdown */}
            <div className="relative" ref={sortRef}>
              <button
                onClick={() => setSortOpen(!sortOpen)}
                className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 text-xs font-semibold text-gray-700 hover:border-gray-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span>Sort by: {sortLabels[sortBy]}</span>
                <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${sortOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {sortOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-44 z-10" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
                  {(Object.entries(sortLabels) as [SortOption, string][]).map(([key, label]) => (
                    <button key={key} onClick={() => { setSortBy(key); setSortOpen(false); }} className={`w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold transition-colors cursor-pointer text-left ${sortBy === key ? 'bg-emerald-50 text-emerald-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      {sortBy === key && <i className="ri-check-line text-emerald-600"></i>}
                      <span className={sortBy === key ? '' : 'ml-4'}>{label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tools List */}
          <div className="space-y-3">
            {filtered.map((tool, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-emerald-200 transition-all group">
                <div className="flex items-start gap-4">
                  {/* Logo */}
                  <BrandLogo logoUrl={tool.logoUrl} logoGrad={tool.logoGrad} logoIcon={tool.logoIcon} name={tool.name} />

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Name + badge */}
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <h3 className="text-base font-bold text-gray-900">{tool.name}</h3>
                          {tool.badge && (
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tool.badgeColor}`}>{tool.badge}</span>
                          )}
                        </div>
                        {/* Tagline */}
                        <p className="text-xs font-medium text-emerald-600 mb-1.5">{tool.tagline}</p>
                        {/* Description */}
                        <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-3">{tool.desc}</p>
                        {/* Tags */}
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {tool.tags.map((tag, ti) => (
                            <span key={ti} className="text-xs font-medium text-gray-500 bg-gray-100 rounded-full px-2.5 py-0.5">{tag}</span>
                          ))}
                        </div>
                        {/* Price */}
                        <p className="text-xs text-gray-500">
                          <i className="ri-price-tag-3-line mr-1 text-gray-400"></i>
                          {tool.price}
                        </p>
                      </div>

                      {/* Right: Rating + Buttons */}
                      <div className="flex flex-col items-end gap-3 flex-shrink-0">
                        {/* Rating */}
                        <div className="text-right">
                          <div className="flex items-center gap-1 justify-end mb-0.5">
                            <div className="flex items-center gap-0.5">
                              {renderStars(tool.rating)}
                            </div>
                            <span className="text-sm font-bold text-gray-800 ml-0.5">{tool.rating}</span>
                          </div>
                          <span className="text-xs text-gray-400">({tool.reviews.toLocaleString()} reviews)</span>
                        </div>

                        {/* Buttons */}
                        <div className="flex items-center gap-2">
                          <a
                            href={`/categories/food-beverage/${tool.slug}`}
                            className="flex items-center gap-1.5 px-3.5 py-2 border border-gray-300 text-gray-600 hover:border-emerald-400 hover:text-emerald-600 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap"
                          >
                            View Details
                          </a>
                          <a
                            href={tool.link}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-external-link-line text-xs"></i>
                            Access
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filtered.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-gray-100">
                  <i className="ri-inbox-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-sm font-semibold text-gray-500">No services found</p>
                <p className="text-xs text-gray-400 mt-1">Try a different filter</p>
              </div>
            )}
          </div>

          {/* RELATED CATEGORIES */}
          <div className="mt-12">
            <h2 className="text-lg font-bold text-gray-900 mb-5">Related categories</h2>
            <div className="grid grid-cols-3 gap-4">
              {relatedCategories.map((cat, i) => (
                <a key={i} href={cat.href} className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-emerald-200 transition-all cursor-pointer group">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${cat.grad} mb-3`}>
                    <i className={`${cat.icon} text-white text-lg`}></i>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1 group-hover:text-emerald-600 transition-colors">{cat.name}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed mb-3">{cat.desc}</p>
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <i className="ri-tools-line"></i>
                    <span>{cat.count} tools</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* FOOTER */}
      <footer style={{ background: '#ecfdf5' }} className="pt-14 pb-6 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid md:grid-cols-12 gap-10 pb-10 border-b border-gray-200">
            <div className="md:col-span-4">
              <div className="flex items-center mb-4">
                <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">
                247digitrend, operated by 247 Digital Marketing Co., Ltd., provides unbiased reviews and comparisons of online tools and digital platforms, helping businesses make informed decisions in the digital world.
              </p>
            </div>
            <div className="md:col-span-2 md:col-start-6">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Resources</h5>
              {[{ label: 'All Reviews', href: '/reviews' }, { label: 'Comparisons', href: '/comparisons' }, { label: 'Categories', href: '/categories' }, { label: 'Blog', href: '/blog' }].map((item, i) => (
                <a key={i} href={item.href} className="block text-sm text-gray-500 hover:text-emerald-600 transition-colors mb-3 cursor-pointer">{item.label}</a>
              ))}
            </div>
            <div className="md:col-span-2">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Company</h5>
              {[{ label: 'About Us', href: '/about' }, { label: 'Contact', href: '/contact' }, { label: 'Submit a Tool', href: '/submit-tool' }, { label: 'Advertise', href: '/advertise' }].map((item, i) => (
                <a key={i} href={item.href} className="block text-sm text-gray-500 hover:text-emerald-600 transition-colors mb-3 cursor-pointer">{item.label}</a>
              ))}
            </div>
            <div className="md:col-span-2">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Follow Us</h5>
              <div className="flex items-center gap-3">
                {['ri-twitter-x-line', 'ri-linkedin-line', 'ri-facebook-line', 'ri-instagram-line'].map((icon, i) => (
                  <a key={i} href="#" className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:text-emerald-600 hover:border-emerald-300 transition-all cursor-pointer">
                    <i className={`${icon} text-base`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div className="pt-5 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-400">© 2026 247digitrend. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-xs text-gray-400 hover:text-emerald-600 transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-xs text-gray-400 hover:text-emerald-600 transition-colors cursor-pointer">Terms of Service</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-emerald-600 transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>

      <FloatingCtaBar onSignUp={() => setShowSignUp(true)} />
    </div>
  );
}
